import { useCallback, useEffect, useState } from 'react';

import { Id } from 'react-toastify';
import { UseNotificationCenter } from 'react-toastify/addons/use-notification-center';

import { useQueryClient } from '@tanstack/react-query';

import { useBodyCssClass } from '@hsp/react-hooks';
import { isDefined, isNotDefined, isNumber } from '@hsp/utilities';

import { INotification } from '@c/notifications/types';

import { IResult, OperationResult } from '@t/api/common';

import {
  useDeleteAllNotifications,
  useDeleteNotification,
  useReadAllNotifications,
  useReadNotification,
} from 'components/notifications/hooks';
import { notificationQueryKeys } from 'components/notifications/utilities';
import { useAppDispatch, useAppSelector } from 'hooks/store';
import { ApplicationActions } from 'store/actions';
import { selectNotificationUnreadOnly } from 'store/selectors';

import { useIsNotificationEmpty } from './empty.hook';
import { useVisibleNotifications } from './visible.hook';

export const useNotificationPanelConfiguration = ({
  clear,
  find,
  notifications,
  markAllAsRead,
  markAsRead,
  remove,
  unreadCount,
}: UseNotificationCenter<INotification>) => {
  const dispatch = useAppDispatch();
  const notificationUnreadOnly = useAppSelector(selectNotificationUnreadOnly);

  const [showUnreadOnly, toggleFilter] = useState(notificationUnreadOnly);

  const { deleteNotification } = useDeleteNotification();
  const { deleteNotifications } = useDeleteAllNotifications();
  const { read } = useReadNotification();
  const { readAll } = useReadAllNotifications();
  const isEmpty = useIsNotificationEmpty(notifications, unreadCount, showUnreadOnly);
  const queryClient = useQueryClient();
  const visible = useVisibleNotifications(notifications, showUnreadOnly);

  const queryKey = notificationQueryKeys.all;

  useBodyCssClass('block-scroll');

  const markDeleted = useCallback(
    (id?: Id) => {
      if (isNotDefined(id)) {
        return deleteNotifications().then(({ success }) => {
          if (success) {
            clear();

            return true;
          }

          return false;
        });
      }

      const notification = find(id);

      if (isNotDefined(notification)) {
        return Promise.reject(false);
      }

      return new Promise<OperationResult>((resolve) => {
        if (isNumber(id)) {
          resolve({ failure: false, success: true });

          return;
        }

        deleteNotification(id).then((result) => resolve(result));
      }).then(({ success }) => {
        if (success) {
          remove(id);
        }

        return success;
      });
    },
    [clear, deleteNotification, deleteNotifications, find, remove]
  );

  const markRead = useCallback(
    (id?: Id) => {
      if (isNotDefined(id)) {
        return readAll().then(({ success }) => {
          if (success) {
            markAllAsRead();

            return true;
          }

          return false;
        });
      }

      const notification = find(id);

      if (isNotDefined(notification)) {
        return Promise.reject(false);
      }

      return new Promise<OperationResult>((resolve) => {
        if (isNumber(id)) {
          resolve({ failure: false, success: true });

          return;
        }

        read(id).then((result) => resolve(result));
      }).then(({ success }) => {
        if (success) {
          markAsRead(id);
        }

        return success;
      });
    },
    [find, markAllAsRead, markAsRead, read, readAll]
  );

  const handleClear = useCallback(() => {
    markDeleted().then((success) => {
      if (success) {
        queryClient.removeQueries({ queryKey });
      }
    });
  }, [markDeleted, queryClient, queryKey]);

  const handleMarkAllAsRead = useCallback(() => {
    markRead().then((success) => {
      if (success) {
        queryClient.setQueryData<IResult<INotification[]>>(queryKey, (oldData) => {
          if (isDefined(oldData)) {
            const { dataContainer } = oldData;

            if (isNotDefined(dataContainer)) {
              return oldData;
            }
            oldData = {
              ...oldData,
              dataContainer: dataContainer.map((item) => {
                return {
                  ...item,
                  readDate: new Date().toISOString(),
                };
              }),
            };

            return oldData;
          }

          return oldData;
        });
      }
    });
  }, [markRead, queryClient, queryKey]);

  const handleMarkAsRead = useCallback((id: Id) => markRead(id), [markRead]);

  const handleRemove = useCallback(
    (id: Id) => {
      markDeleted(id).then((success) => {
        if (success) {
          queryClient.setQueryData<IResult<INotification[]>>(queryKey, (oldData) => {
            if (isDefined(oldData)) {
              const { dataContainer } = oldData;

              if (isNotDefined(dataContainer)) {
                return oldData;
              }
              oldData = {
                ...oldData,
                dataContainer: [...dataContainer.filter(({ id: itemId }) => itemId !== id)],
              };

              return oldData;
            }

            return oldData;
          });
        }
      });
    },
    [markDeleted, queryClient, queryKey]
  );

  const handleToggleUnreadOnly = useCallback(
    (toggle: boolean) => {
      toggleFilter(toggle);
      dispatch(ApplicationActions.setNotificationUnreadOnly(toggle));
    },
    [dispatch]
  );

  useEffect(() => toggleFilter(notificationUnreadOnly), [notificationUnreadOnly]);

  return {
    handleClear,
    handleMarkAllAsRead,
    handleMarkAsRead,
    handleRemove,
    handleToggleUnreadOnly,
    isEmpty,
    showUnreadOnly,
    toggleFilter,
    visible,
  };
};
